import React from 'react';
import { useNavigate } from 'react-router-dom';

import { isFirstStepSkipped } from '@/core/utils/rapidstor';

import classes from './styles.module.scss';

interface Props {
    progress: number;
    step: number;
    children: React.ReactNode;
    actualStep?: number;
    'data-i18n-key'?: string;
    // Forwarded for Automate's inspector, like data-i18n-key (see Button).
    'data-rs-setting-dynamic'?: string;
}

function JourneyStep({
    step, progress, children, actualStep, 'data-i18n-key': dataI18nKey,
    'data-rs-setting-dynamic': dataRsSettingDynamic,
}: Props) {
    const navigate = useNavigate();

    // Use actualStep if provided, otherwise use progress (for backward compatibility)
    const displayStep = actualStep ?? progress;

    const firstStepSkipped = isFirstStepSkipped();

    const getClass = (el: string) => {
    // When storageType is disabled and we're on Storage Size (actualStep 2),
    // it should show as active (completed) with full opacity even if we're on step 1
        if (firstStepSkipped && displayStep === 2 && step === 1) {
            return el === 'number'
                ? classes.active_number_full_opacity
                : classes.active_label_full_opacity;
        }

        // Current step: the step we're currently on
        if (step === displayStep) {
            return el === 'number' ? classes.current_number : classes.current_label;
        }
        // Active step: a previous/completed step (only show active if it's a previous step)
        if (step > displayStep) {
            return el === 'number' ? classes.active_number : classes.active_label;
        }
        // Future step: no special styling
        return '';
    };

    const commonClasses = [classes.journeyStep].join(' ');

    /**
   * Method that navigates a user back to a step in the booking journey, uses step and progress props
   */
    const goTo = () => {
    // Navigate based on actual step number, not displayed step number
        const targetStep = actualStep ?? progress;

        if (step > targetStep && targetStep === 1) {
            navigate('/');
        }

        if (step > targetStep && targetStep === 2) {
            if (window.rapidstor_settings.app_config.pages.storageType) {
                navigate('/unit-listing');
            } else {
                navigate('/');
            }
        }
    };

    return (
        <div className={`${commonClasses}`} onClick={goTo}>
            <div className={`${classes.number} ${getClass('number')}`}>
                {progress}
            </div>
            <div className={`${classes.label} ${getClass('label')}`} data-i18n-key={dataI18nKey} data-rs-setting-dynamic={dataRsSettingDynamic}>{children}</div>
        </div>
    );
}

export default JourneyStep;
